'use client'

import { getLogger } from '@logtape/logtape'
import { env } from '@shared/config/env'
import { useCallback, useEffect, useState } from 'react'

const logger = getLogger(['web', 'external-dialog', 'hook'])

type Props<DialogId> = {
  dialogId: DialogId
  initialOpen?: boolean
  dialogWidth?: number
  dialogHeight?: number
  dialogType?: 'ALERT' | 'CONFIRM'
  closeCallback?: () => void
}

export type BridgeMessage =
  | 'PARENT_TO_CHILD_DIALOG_TYPE_COMMAND'
  | 'PARENT_TO_CHILD_DIALOG_OPEN_COMMAND'
  | 'PARENT_TO_CHILD_DIALOG_CLOSE_COMMAND'
  | 'PARENT_TO_CHILD_SET_DIALOG_HEIGHT_COMMAND'
  | 'PARENT_TO_CHILD_SET_DIALOG_WIDTH_COMMAND'
  | 'CHILD_TO_PARENT_DIALOG_TYPE_RESPONSE'
  | 'CHILD_TO_PARENT_DIALOG_OPENED_RESPONSE'
  | 'CHILD_TO_PARENT_DIALOG_CLOSED_RESPONSE'
  | 'CHILD_TO_PARENT_DIALOG_SUBMIT_RESPONSE'
  | 'CHILD_TO_PARENT_SET_DIALOG_HEIGHT_RESPONSE'
  | 'CHILD_TO_PARENT_SET_DIALOG_WIDTH_RESPONSE'
  | 'CHILD_TO_PARENT_INTERNAL_NAVIGATE_RESPONSE'
  | 'CHILD_TO_PARENT_EXTERNAL_NAVIGATE_RESPONSE'

type BridgeMessageWithDialogIdPrefix<Prefix extends string> = `${Prefix}_${BridgeMessage}`

export const BRIDGE_MESSAGE_MAP = {
  PARENT_TO_CHILD_DIALOG_TYPE_COMMAND: 'PARENT_TO_CHILD_DIALOG_TYPE_COMMAND',
  PARENT_TO_CHILD_DIALOG_OPEN_COMMAND: 'PARENT_TO_CHILD_DIALOG_OPEN_COMMAND',
  PARENT_TO_CHILD_DIALOG_CLOSE_COMMAND: 'PARENT_TO_CHILD_DIALOG_CLOSE_COMMAND',
  PARENT_TO_CHILD_SET_DIALOG_HEIGHT_COMMAND: 'PARENT_TO_CHILD_SET_DIALOG_HEIGHT_COMMAND',
  PARENT_TO_CHILD_SET_DIALOG_WIDTH_COMMAND: 'PARENT_TO_CHILD_SET_DIALOG_WIDTH_COMMAND',
  CHILD_TO_PARENT_DIALOG_TYPE_RESPONSE: 'CHILD_TO_PARENT_DIALOG_TYPE_RESPONSE',
  CHILD_TO_PARENT_DIALOG_OPENED_RESPONSE: 'CHILD_TO_PARENT_DIALOG_OPENED_RESPONSE',
  CHILD_TO_PARENT_DIALOG_CLOSED_RESPONSE: 'CHILD_TO_PARENT_DIALOG_CLOSED_RESPONSE',
  CHILD_TO_PARENT_DIALOG_SUBMIT_RESPONSE: 'CHILD_TO_PARENT_DIALOG_SUBMIT_RESPONSE',
  CHILD_TO_PARENT_SET_DIALOG_HEIGHT_RESPONSE: 'CHILD_TO_PARENT_SET_DIALOG_HEIGHT_RESPONSE',
  CHILD_TO_PARENT_SET_DIALOG_WIDTH_RESPONSE: 'CHILD_TO_PARENT_SET_DIALOG_WIDTH_RESPONSE',
  CHILD_TO_PARENT_INTERNAL_NAVIGATE_RESPONSE: 'CHILD_TO_PARENT_INTERNAL_NAVIGATE_RESPONSE',
  CHILD_TO_PARENT_EXTERNAL_NAVIGATE_RESPONSE: 'CHILD_TO_PARENT_EXTERNAL_NAVIGATE_RESPONSE',
} as const

/**
 * @description 컨테이너로부터 메시지를 받아 외부 다이얼로그를 제어합니다.
 */
export const useExternalDialog = <DialogId extends string, SubmitPayload extends object>({
  dialogId,
  initialOpen = false,
  dialogWidth = 320,
  dialogHeight = 380,
  dialogType = 'ALERT',
  closeCallback,
}: Props<DialogId>) => {
  const [isOpen, setIsOpen] = useState(initialOpen)

  const getBridgeMessage = useCallback(
    (message: BridgeMessage): BridgeMessageWithDialogIdPrefix<DialogId> => {
      return `${dialogId}_${message}`
    },
    [dialogId],
  )

  const sendMessageToMultipleDomains = useCallback(
    (message: { type: BridgeMessageWithDialogIdPrefix<DialogId>; payload?: object }) => {
      const domains = [env.NEXT_PUBLIC_PODO_WEB_URL, env.NEXT_PUBLIC_API_URL]

      domains.forEach((domain) => {
        if (window.parent && domain) {
          window.parent.postMessage(message, domain)
        }
      })
    },
    [],
  )

  const handleOpen = useCallback(() => {
    setIsOpen(true)
    sendMessageToMultipleDomains({
      type: getBridgeMessage(BRIDGE_MESSAGE_MAP.CHILD_TO_PARENT_DIALOG_OPENED_RESPONSE),
    })
  }, [getBridgeMessage, sendMessageToMultipleDomains])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    closeCallback?.()
    sendMessageToMultipleDomains({
      type: getBridgeMessage(BRIDGE_MESSAGE_MAP.CHILD_TO_PARENT_DIALOG_CLOSED_RESPONSE),
    })
  }, [getBridgeMessage, closeCallback, sendMessageToMultipleDomains])

  const handleSetDialogHeight = useCallback(() => {
    sendMessageToMultipleDomains({
      type: getBridgeMessage(BRIDGE_MESSAGE_MAP.CHILD_TO_PARENT_SET_DIALOG_HEIGHT_RESPONSE),
      payload: {
        height: dialogHeight,
      },
    })
  }, [getBridgeMessage, dialogHeight, sendMessageToMultipleDomains])

  const handleSetDialogWidth = useCallback(() => {
    sendMessageToMultipleDomains({
      type: getBridgeMessage(BRIDGE_MESSAGE_MAP.CHILD_TO_PARENT_SET_DIALOG_WIDTH_RESPONSE),
      payload: { width: dialogWidth },
    })
  }, [getBridgeMessage, dialogWidth, sendMessageToMultipleDomains])

  const handleSetDialogType = useCallback(() => {
    sendMessageToMultipleDomains({
      type: getBridgeMessage(BRIDGE_MESSAGE_MAP.CHILD_TO_PARENT_DIALOG_TYPE_RESPONSE),
      payload: { dialogType },
    })
  }, [getBridgeMessage, dialogType, sendMessageToMultipleDomains])

  const handleSubmit = useCallback(
    (payload: SubmitPayload) => {
      sendMessageToMultipleDomains({
        type: getBridgeMessage(BRIDGE_MESSAGE_MAP.CHILD_TO_PARENT_DIALOG_SUBMIT_RESPONSE),
        payload,
      })
      handleClose()
    },
    [getBridgeMessage, handleClose, sendMessageToMultipleDomains],
  )

  const handleExternalNavigate = useCallback(
    <QueryParams>(url: string, query: QueryParams) => {
      sendMessageToMultipleDomains({
        type: getBridgeMessage(BRIDGE_MESSAGE_MAP.CHILD_TO_PARENT_EXTERNAL_NAVIGATE_RESPONSE),
        payload: { url, query },
      })
      handleClose()
    },
    [getBridgeMessage, handleClose, sendMessageToMultipleDomains],
  )

  const handleInternalNavigate = useCallback(
    <QueryParams>(url: string, query: QueryParams) => {
      sendMessageToMultipleDomains({
        type: getBridgeMessage(BRIDGE_MESSAGE_MAP.CHILD_TO_PARENT_INTERNAL_NAVIGATE_RESPONSE),
        payload: { url, query },
      })
      handleClose()
    },
    [getBridgeMessage, handleClose, sendMessageToMultipleDomains],
  )
  useEffect(() => {
    handleSetDialogHeight()
  }, [handleSetDialogHeight])

  useEffect(() => {
    handleSetDialogWidth()
  }, [handleSetDialogWidth])

  useEffect(() => {
    handleSetDialogType()
  }, [handleSetDialogType])

  const handleMessageParentCommand = useCallback(
    (event: MessageEvent) => {
      const type = event.data.type as BridgeMessageWithDialogIdPrefix<DialogId>

      switch (type) {
        case getBridgeMessage(BRIDGE_MESSAGE_MAP.PARENT_TO_CHILD_DIALOG_OPEN_COMMAND): {
          handleOpen()
          break
        }
        case getBridgeMessage(BRIDGE_MESSAGE_MAP.PARENT_TO_CHILD_DIALOG_CLOSE_COMMAND): {
          handleClose()
          break
        }
        case getBridgeMessage(BRIDGE_MESSAGE_MAP.CHILD_TO_PARENT_DIALOG_SUBMIT_RESPONSE): {
          break
        }
        case getBridgeMessage(BRIDGE_MESSAGE_MAP.PARENT_TO_CHILD_SET_DIALOG_HEIGHT_COMMAND): {
          handleSetDialogHeight()
          break
        }
        case getBridgeMessage(BRIDGE_MESSAGE_MAP.PARENT_TO_CHILD_SET_DIALOG_WIDTH_COMMAND): {
          handleSetDialogWidth()
          break
        }
        case getBridgeMessage(BRIDGE_MESSAGE_MAP.PARENT_TO_CHILD_DIALOG_TYPE_COMMAND): {
          handleSetDialogType()
          break
        }
        default: {
          logger.error('unknown-external-dialog-message-type', { type })
          break
        }
      }
    },
    [getBridgeMessage, handleClose, handleOpen, handleSetDialogHeight, handleSetDialogType, handleSetDialogWidth],
  )

  useEffect(() => {
    window.addEventListener('message', handleMessageParentCommand)
    return () => {
      window.removeEventListener('message', handleMessageParentCommand)
    }
  }, [handleMessageParentCommand])

  return { isOpen, handleOpen, handleClose, handleSubmit, handleExternalNavigate, handleInternalNavigate }
}
